import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, ChevronLeft, ChevronRight, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";

const STEPS = ["pooja", "date", "info", "review", "payment"];
const STEP_LABELS = {
  pooja: "selectPooja",
  date: "selectDate",
  info: "devoteeInfo",
  review: "review",
  payment: "payment",
};

function fmt(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function Booking() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const preselect = params.get("pooja") || "";

  const [step, setStep] = useState(preselect ? 1 : 0);
  const [poojas, setPoojas] = useState([]);
  const [settings, setSettings] = useState(null);
  const [chosen, setChosen] = useState(preselect);
  const [date, setDate] = useState();
  const [availability, setAvailability] = useState(null);
  const [monthData, setMonthData] = useState({ capacities: [], statuses: [] });
  const [viewMonth, setViewMonth] = useState(new Date());
  const [info, setInfo] = useState({ devotee_name: "", mobile: "", email: "", address: "", gotra: "", notes: "" });
  const [participants, setParticipants] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);
  const visibleSteps = useMemo(
    () => (settings?.deposit_enabled ? STEPS : STEPS.filter((s) => s !== "payment")),
    [settings]
  );

  useEffect(() => {
    api.get("/poojas").then(({ data }) => setPoojas(data));
    api.get("/settings/public").then(({ data }) => setSettings(data));
  }, []);

  useEffect(() => {
    const y = viewMonth.getFullYear();
    const m = viewMonth.getMonth() + 1;
    api.get(`/calendar/month?year=${y}&month=${m}`).then(({ data }) => setMonthData(data)).catch(() => {});
  }, [viewMonth]);

  useEffect(() => {
    if (chosen && date) {
      api.get(`/calendar/availability?date=${fmt(date)}&pooja_slug=${chosen}`).then(({ data }) => setAvailability(data));
    }
  }, [chosen, date]);

  const poojaObj = useMemo(() => poojas.find((p) => p.slug === chosen), [poojas, chosen]);

  // Compute days that are bookable (have capacity, not closed)
  const bookableDates = useMemo(() => {
    const set = new Set();
    for (const c of monthData.capacities) {
      if (c.pooja_slug === chosen && !c.closed && (c.capacity - c.booked) > 0) set.add(c.date);
    }
    const closedSet = new Set(monthData.statuses.filter((s) => s.status !== "open").map((s) => s.date));
    return { open: set, closed: closedSet };
  }, [monthData, chosen]);

  const isDisabledDay = (d) => {
    const ds = fmt(d);
    if (bookableDates.closed.has(ds)) return true;
    const today0 = new Date(new Date().setHours(0, 0, 0, 0));
    if (d < today0) return true;
    // Advance-booking cutoff for the selected pooja
    if (poojaObj && (poojaObj.booking_cutoff_days ?? 5) > 0) {
      const minDate = new Date(today0);
      minDate.setDate(minDate.getDate() + (poojaObj.booking_cutoff_days ?? 5));
      if (d < minDate) return true;
    }
    if (!bookableDates.open.has(ds)) return true;
    return false;
  };

  const submitBooking = async () => {
    setSubmitting(true);
    try {
      const payload = {
        pooja_slug: chosen,
        booking_date: fmt(date),
        ...info,
        participants,
      };
      const { data } = await api.post("/bookings", payload);
      setBooking(data);
      if (data.deposit_required) {
        setStep(4);
      } else {
        nav(`/book/success/${data.reference}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const startPayment = async () => {
    try {
      const { data } = await api.post("/payments/create-order", { booking_id: booking.id });
      if (!window.Razorpay) {
        await new Promise((res, rej) => {
          const s = document.createElement("script");
          s.src = "https://checkout.razorpay.com/v1/checkout.js";
          s.onload = res; s.onerror = rej;
          document.body.appendChild(s);
        });
      }
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: "Acharya Guruji",
        description: `Deposit for ${booking.pooja_name}`,
        prefill: { name: booking.devotee_name, contact: booking.mobile, email: booking.email || "" },
        theme: { color: "#8E2B25" },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", {
              booking_id: booking.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            nav(`/book/success/${booking.reference}`);
          } catch (e) {
            toast.error("Payment verification failed");
          }
        },
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="booking-page">
      <section className="section bg-paper">
        <div className="container-x max-w-5xl">
          <div className="text-overline mb-3">{t("nav.book")}</div>
          <h1 className="font-heading text-4xl md:text-5xl tracking-tight">{t("booking.title")}</h1>

          {/* Stepper */}
          <ol className="mt-10 flex flex-wrap gap-x-3 gap-y-2 text-sm">
            {visibleSteps.map((s, i) => (
              <li key={s} className="flex items-center gap-2">
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs ${i <= step ? "bg-[color:var(--brand)] text-[#faf8f5]" : "bg-[color:var(--bg-2)] text-[color:var(--ink-2)]"}`}>
                  {i < step ? <Check size={12} /> : i + 1}
                </span>
                <span className={i <= step ? "" : "opacity-60"}>{t(`booking.${STEP_LABELS[s]}`)}</span>
                {i < visibleSteps.length - 1 && <ChevronRight size={14} className="opacity-40" />}
              </li>
            ))}
          </ol>

          <div className="mt-10 card-elevated rounded-md p-6 md:p-8">
            {step === 0 && (
              <div data-testid="step-pooja">
                <h2 className="font-heading text-2xl mb-5">{t("booking.selectPooja")}</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {poojas.filter((p) => p.booking_enabled).map((p) => (
                    <button
                      key={p.slug}
                      data-testid={`select-pooja-${p.slug}`}
                      onClick={() => { setChosen(p.slug); setStep(1); }}
                      className={`text-left rounded-md border p-4 transition-colors ${chosen === p.slug ? "border-[color:var(--brand)] bg-[color:var(--bg-2)]" : "border-[color:var(--border)] hover:border-[color:var(--brand)]/50"}`}
                    >
                      <div className="font-heading text-lg">{p.name}</div>
                      <div className="text-xs opacity-70 mt-1 line-clamp-2">{p.short_description}</div>
                      <div className="mt-2 text-sm text-[color:var(--brand)]">From {t("common.rupee")}{p.price.toLocaleString("en-IN")}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 1 && (
              <div data-testid="step-date">
                <h2 className="font-heading text-2xl mb-2">{t("booking.selectDate")}</h2>
                <p className="text-sm opacity-70 mb-4">{poojaObj?.name}</p>
                <div className="grid md:grid-cols-2 gap-6 items-start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    onMonthChange={setViewMonth}
                    disabled={isDisabledDay}
                    className="rounded-md border border-[color:var(--border)] bg-white"
                  />
                  <div>
                    {date ? (
                      <div className="space-y-3">
                        <div className="text-sm opacity-70">Selected:</div>
                        <div className="font-heading text-2xl">{date.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
                        {availability ? (
                          availability.available ? (
                            <div className="text-sm text-green-700">{t("booking.slotsRemaining", { count: availability.remaining })}</div>
                          ) : (
                            <div className="text-sm text-red-700">
                              {availability.reason === "closed" ? t("booking.dateClosed") :
                               availability.reason === "cutoff" ? t("booking.cutoffMessage", { days: availability.cutoff_days }) :
                               t("booking.noSlots")}
                            </div>
                          )
                        ) : (
                          <div className="text-sm opacity-60">Checking availability...</div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <div className="text-sm opacity-70">Pick an available date from the calendar. Dates without availability are disabled.</div>
                        {poojaObj && (poojaObj.booking_cutoff_days ?? 5) > 0 && (
                          <div className="mt-3 text-xs px-3 py-2 rounded-md bg-[color:var(--bg-2)] border border-[color:var(--border)] inline-block">
                            {t("booking.cutoffNotice", { days: poojaObj.booking_cutoff_days ?? 5 })}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="mt-6 flex gap-3">
                      <button onClick={() => setStep(0)} className="px-4 py-2 rounded-full text-sm border border-[color:var(--border)] inline-flex items-center gap-1" data-testid="date-back">
                        <ChevronLeft size={14} /> {t("common.back")}
                      </button>
                      <button
                        onClick={() => setStep(2)}
                        disabled={!date || !availability?.available}
                        className="px-5 py-2 rounded-full btn-brand text-sm disabled:opacity-50"
                        data-testid="date-next-btn"
                      >
                        {t("common.next")} <ChevronRight size={14} className="inline" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="step-info">
                <h2 className="font-heading text-2xl mb-5">{t("booking.devoteeInfo")}</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <Input placeholder={t("booking.name")} required value={info.devotee_name} onChange={(e) => setInfo({ ...info, devotee_name: e.target.value })} data-testid="info-name" />
                  <Input placeholder={t("booking.mobile")} required value={info.mobile} onChange={(e) => setInfo({ ...info, mobile: e.target.value })} data-testid="info-mobile" />
                  <Input placeholder={t("booking.emailLabel")} type="email" value={info.email} onChange={(e) => setInfo({ ...info, email: e.target.value })} data-testid="info-email" />
                  <Input placeholder={t("booking.gotra")} value={info.gotra} onChange={(e) => setInfo({ ...info, gotra: e.target.value })} data-testid="info-gotra" />
                  <div className="md:col-span-2">
                    <Input placeholder={t("booking.address")} value={info.address} onChange={(e) => setInfo({ ...info, address: e.target.value })} data-testid="info-address" />
                  </div>
                  <div className="md:col-span-2">
                    <Textarea placeholder={t("booking.notes")} value={info.notes} onChange={(e) => setInfo({ ...info, notes: e.target.value })} data-testid="info-notes" />
                  </div>
                </div>

                <div className="mt-8">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading text-xl">Additional Participants</h3>
                    <button
                      type="button"
                      onClick={() => setParticipants([...participants, { name: "", relation: "", age: "" }])}
                      className="text-sm inline-flex items-center gap-1 text-[color:var(--brand)] hover:underline"
                      data-testid="add-participant-btn"
                    >
                      <UserPlus size={14} /> {t("booking.addParticipant")}
                    </button>
                  </div>
                  {participants.map((p, idx) => (
                    <div key={idx} className="grid md:grid-cols-4 gap-3 mb-3 items-start" data-testid={`participant-row-${idx}`}>
                      <Input placeholder={t("booking.participantName")} value={p.name} onChange={(e) => { const cp = [...participants]; cp[idx].name = e.target.value; setParticipants(cp); }} />
                      <Input placeholder={t("booking.participantRelation")} value={p.relation} onChange={(e) => { const cp = [...participants]; cp[idx].relation = e.target.value; setParticipants(cp); }} />
                      <Input placeholder={t("booking.participantAge")} value={p.age} onChange={(e) => { const cp = [...participants]; cp[idx].age = e.target.value; setParticipants(cp); }} />
                      <button type="button" onClick={() => setParticipants(participants.filter((_, i) => i !== idx))} className="px-3 py-2 text-sm border border-[color:var(--border)] rounded-md inline-flex items-center gap-1" data-testid={`remove-participant-${idx}`}>
                        <Trash2 size={14} /> {t("booking.remove")}
                      </button>
                    </div>
                  ))}
                </div>

                <div className="mt-8 flex gap-3">
                  <button onClick={() => setStep(1)} className="px-4 py-2 rounded-full text-sm border border-[color:var(--border)] inline-flex items-center gap-1" data-testid="info-back">
                    <ChevronLeft size={14} /> {t("common.back")}
                  </button>
                  <button
                    onClick={() => setStep(3)}
                    disabled={!info.devotee_name || !info.mobile}
                    className="px-5 py-2 rounded-full btn-brand text-sm disabled:opacity-50"
                    data-testid="info-next-btn"
                  >
                    {t("common.next")} <ChevronRight size={14} className="inline" />
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="step-review">
                <h2 className="font-heading text-2xl mb-5">{t("booking.review")}</h2>
                <dl className="grid md:grid-cols-2 gap-4 text-sm">
                  <Row k="Pooja" v={poojaObj?.name} />
                  <Row k="Date" v={date?.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} />
                  <Row k={t("booking.name")} v={info.devotee_name} />
                  <Row k={t("booking.mobile")} v={info.mobile} />
                  {info.email && <Row k={t("booking.emailLabel")} v={info.email} />}
                  {info.gotra && <Row k={t("booking.gotra")} v={info.gotra} />}
                  {info.address && <Row k={t("booking.address")} v={info.address} />}
                  {info.notes && <Row k={t("booking.notes")} v={info.notes} />}
                  {participants.length > 0 && (
                    <Row k="Additional" v={participants.map((p) => `${p.name}${p.relation ? ` (${p.relation})` : ""}`).join(", ")} />
                  )}
                </dl>
                {settings?.deposit_enabled && (
                  <div className="mt-6 p-4 rounded-md border border-[color:var(--brand)]/30 bg-[color:var(--bg-2)]">
                    <div className="font-heading text-lg">{t("booking.depositRequired")}</div>
                    <div className="text-sm opacity-80 mt-1">{t("booking.depositNote")}</div>
                    <div className="mt-3 font-heading text-2xl text-[color:var(--brand)]">{t("common.rupee")}{Number(poojaObj?.deposit_amount || settings.default_deposit_amount).toLocaleString("en-IN")}</div>
                  </div>
                )}
                {!settings?.deposit_enabled && (
                  <div className="mt-6 p-4 rounded-md border border-[color:var(--border)] bg-[color:var(--bg-2)] text-sm opacity-85">
                    {t("booking.paymentLater")}
                  </div>
                )}
                <div className="mt-8 flex gap-3">
                  <button onClick={() => setStep(2)} className="px-4 py-2 rounded-full text-sm border border-[color:var(--border)] inline-flex items-center gap-1" data-testid="review-back">
                    <ChevronLeft size={14} /> {t("common.back")}
                  </button>
                  <button
                    onClick={submitBooking}
                    disabled={submitting}
                    className="px-5 py-2 rounded-full btn-brand text-sm disabled:opacity-50"
                    data-testid="confirm-booking-btn"
                  >
                    {submitting ? t("common.loading") : t("booking.confirmDetails")} <ChevronRight size={14} className="inline" />
                  </button>
                </div>
              </div>
            )}

            {step === 4 && booking && (
              <div data-testid="step-payment">
                <h2 className="font-heading text-2xl">{t("booking.payment")}</h2>
                <p className="mt-2 opacity-80 text-sm">{t("booking.depositNote")}</p>
                <div className="mt-5 p-5 rounded-md border border-[color:var(--border)] bg-[color:var(--bg-2)] flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] opacity-60">{t("booking.totalAmount")}</div>
                    <div className="font-heading text-3xl text-[color:var(--brand)]">{t("common.rupee")}{Number(booking.deposit_amount).toLocaleString("en-IN")}</div>
                  </div>
                  <button onClick={startPayment} className="px-5 py-2.5 rounded-full btn-brand text-sm" data-testid="pay-now-btn">
                    {t("booking.completePayment")}
                  </button>
                </div>
                <div className="mt-3 text-xs opacity-65">Reference: {booking.reference}</div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex flex-col">
      <dt className="text-xs uppercase tracking-[0.18em] opacity-60">{k}</dt>
      <dd className="mt-0.5">{v || "—"}</dd>
    </div>
  );
}
